import React, { useState, useEffect } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  CardFooter
} from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { User, Plus, UserCheck, UserPlus, Building2, Share2, Users as UsersIcon, Shield, Bot, MessageSquare, UserX, Search } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserMessagesContext } from '@/contexts/UserMessagesContext';
import UserConnections from '@/components/users/UserConnections';
import UserMessaging from '@/components/users/UserMessaging';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';

// Define form schemas
const inviteFormSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  name: z.string().min(1, { message: "Name is required" }),
  role: z.enum(["Member", "Admin", "Owner"], {
    required_error: "Please select a role",
  }),
  sendEmail: z.boolean().default(true),
  message: z.string().optional(),
});

const organizationFormSchema = z.object({
  name: z.string().min(1, { message: "Organization name is required" }),
  description: z.string().optional(),
});

const shareKbFormSchema = z.object({
  knowledgeBaseId: z.string().min(1, { message: "Please select a knowledge base" }),
  accessLevel: z.enum(["View", "Edit", "Manage"], {
    required_error: "Please select an access level",
  }),
});

const Users = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { requestConnection, onlineUsers, connections } = useUserMessagesContext();
  const { organizations, loading: orgsLoading, createOrganization, getOrganizationMembers, inviteUserToOrganization } = useOrganizations();
  const { knowledgeBases, loading: kbLoading, shareKnowledgeBase } = useKnowledgeBases();
  
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [organizationDialogOpen, setOrganizationDialogOpen] = useState(false);
  const [shareKbDialogOpen, setShareKbDialogOpen] = useState(false);
  const [activeOrganization, setActiveOrganization] = useState<any>(null);
  const [manageMembersDialogOpen, setManageMembersDialogOpen] = useState(false);
  const [messagingDialogOpen, setMessagingDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{id: string, name: string} | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [orgMembers, setOrgMembers] = useState<any[]>([]); // This seems redundant with `users`
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("members");

  // New states for user search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchedUsers, setSearchedUsers] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounce function
  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  };

  const inviteForm = useForm<z.infer<typeof inviteFormSchema>>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      email: "",
      name: "",
      role: "Member",
      sendEmail: true,
      message: "",
    },
  });

  const organizationForm = useForm<z.infer<typeof organizationFormSchema>>({
    resolver: zodResolver(organizationFormSchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const shareKbForm = useForm<z.infer<typeof shareKbFormSchema>>({
    resolver: zodResolver(shareKbFormSchema),
    defaultValues: {
      knowledgeBaseId: "",
      accessLevel: "View",
    },
  });

  // Fetch organization members when active organization changes
  useEffect(() => {
    const fetchOrgMembers = async () => {
      if (activeOrganization) {
        setIsLoading(true);
        try {
          const members = await getOrganizationMembers(activeOrganization.id);
          
          const formattedUsers = members
            .filter(member => member.status === 'active') // Only show active members
            .map(member => {
              const profile = member.profile;
              const lastActiveRaw = profile.updated_at || profile.created_at;
              let lastActive = "N/A";
              const isOnline = onlineUsers.has(profile.id);
              const status = isOnline ? "Active" : "Inactive";

              if (lastActiveRaw) {
                const lastActiveDate = new Date(lastActiveRaw);
                lastActive = format(lastActiveDate, 'PP');
              }
              return {
                id: profile.id,
                name: profile.full_name || profile.username || 'Unnamed User',
                email: '', // Email is not exposed for privacy
                avatar: profile.avatar_url || '',
                role: member.role, // This is the user's role in the organization
                status, // This is their general app activity status
                lastActive,
              };
            });
          setUsers(formattedUsers);
        } catch (error) {
          console.error('Error fetching organization members:', error);
          toast({
            title: 'Error fetching members',
            description: 'There was a problem loading the member list',
            variant: 'destructive'
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        setUsers([]);
        setIsLoading(false);
      }
    };

    if (organizations.length > 0 && !activeOrganization) {
      setActiveOrganization(organizations[0]);
    }
    fetchOrgMembers();
  }, [activeOrganization, getOrganizationMembers, toast, organizations, onlineUsers]);

  // New useEffect for user search
  useEffect(() => {
    const fetchUsers = async () => {
      if (!searchTerm || searchTerm.length < 3) { // Require at least 3 characters for search
        setSearchedUsers([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, full_name, avatar_url, email')
          .ilike('full_name', `${searchTerm}%`) // Case-insensitive search
          .limit(10); // Limit results for performance

        if (error) {
          console.error('Error searching users:', error);
          toast({
            title: 'Error searching users',
            description: 'There was a problem fetching search results',
            variant: 'destructive'
          });
          setSearchedUsers([]);
          return;
        }

        // Filter out current user and already connected/pending users
        const filteredUsers = data.filter(profile => {
          if (profile.id === user?.id) return false; // Don't show self
          const isConnected = connections.some(conn => 
            (conn.user_id === profile.id || conn.connected_user_id === profile.id) && 
            (conn.status === 'accepted' || conn.status === 'pending')
          );
          return !isConnected;
        });

        setSearchedUsers(filteredUsers);
      } catch (error) {
        console.error('Exception during user search:', error);
        setSearchedUsers([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debouncedFetchUsers = debounce(fetchUsers, 500);
    debouncedFetchUsers();

    return () => {
      // Cleanup for debounce
      // (Not strictly necessary for this simple debounce, but good practice)
    };
  }, [searchTerm, user?.id, connections, toast]);

  const handleInviteUser = async (data: z.infer<typeof inviteFormSchema>) => {
    if (!activeOrganization) {
      toast({
        title: "No Organization Selected",
        description: "Please select an organization before inviting users.",
        variant: "destructive"
      });
      return;
    }
    await inviteUserToOrganization(
      activeOrganization.id,
      data.email,
      data.role,
      activeOrganization.name
    );
    setInviteDialogOpen(false);
    inviteForm.reset();
  };

  const handleCreateOrganization = async (data: z.infer<typeof organizationFormSchema>) => {
    const orgId = await createOrganization(data.name, data.description);
    
    if (orgId) {
      const newOrg = organizations.find(org => org.id === orgId);
      if (newOrg) {
        setActiveOrganization(newOrg);
      }
      
      toast({
        title: "Organization created",
        description: `${data.name} has been created successfully.`,
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to create organization",
        variant: "destructive"
      });
    }
    
    setOrganizationDialogOpen(false);
    organizationForm.reset();
  };

  const handleShareKnowledgeBase = async (data: z.infer<typeof shareKbFormSchema>) => {
    if (!activeOrganization) {
      toast({
        title: "Error",
        description: "Please select an organization first",
        variant: "destructive"
      });
      return;
    }
    
    const success = await shareKnowledgeBase(
      data.knowledgeBaseId,
      activeOrganization.id,
      data.accessLevel
    );
    
    if (success) {
      const kb = knowledgeBases.find(kb => kb.id === data.knowledgeBaseId);
      toast({
        title: "Knowledge base shared",
        description: `${kb?.name} has been shared with ${activeOrganization?.name}.`,
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to share knowledge base",
        variant: "destructive"
      });
    }
    
    setShareKbDialogOpen(false);
    shareKbForm.reset();
  };

  const handleConnect = async (userId: string, userName: string) => {
    if (user?.id === userId) {
      toast({
        title: "Cannot connect with yourself",
        description: "You cannot send a connection request to yourself",
        variant: "destructive"
      });
      return;
    }
    
    const result = await requestConnection(userId);
    
    if (result) {
      toast({
        title: "Connection request sent",
        description: `A connection request has been sent to ${userName}`,
      });
    }
  };
  
  const handleStartMessaging = (userId: string, userName: string) => {
    setSelectedUser({ id: userId, name: userName });
    setMessagingDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-500';
      case 'Inactive':
        return 'bg-gray-500';
      case 'Pending':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'Owner':
        return 'default';
      case 'Admin':
        return 'secondary';
      case 'Member':
        return 'outline';
      case 'Pending':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'Edit':
        return 'text-green-600';
      case 'View':
        return 'text-blue-600';
      case 'Manage':
        return 'text-purple-600';
      default:
        return 'text-gray-600';
    }
  };

  const getUserRoleInOrg = (org: any): 'Owner' | 'Admin' | 'Member' => {
    if (org.created_by === user?.id) return 'Owner';
    return 'Member';
  };

  const renderKnowledgeBases = () => {
    return knowledgeBases.map(kb => (
      <Card key={kb.id} className="overflow-hidden">
        <CardHeader>
          <div className="flex items-start justify-between">
            <CardTitle>{kb.name}</CardTitle>
            {kb.is_private && (
              <Badge variant="outline" className="text-blue-600">
                Private
              </Badge>
            )}
          </div>
          <CardDescription>{kb.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Shared with</span>
            <span className="font-medium">{kb.shared_with_count || 0} users</span>
          </div>
        </CardContent>
        <CardFooter className="border-t pt-4 flex justify-between">
          <Button variant="outline" size="sm">
            Manage Access
          </Button>
          <Button variant={kb.is_private ? "outline" : "ghost-destructive"} size="sm">
            {kb.is_private ? 'Share' : 'Make Private'}
          </Button>
        </CardFooter>
      </Card>
    ));
  };

  return (
    <MainLayout>
      <div className="h-full flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Users & Organizations</h1>
            <p className="text-muted-foreground">
              Manage users and organizations within your workspace
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOrganizationDialogOpen(true)} className="gap-2">
              <Building2 size={16} />
              Create Organization
            </Button>
            <Button onClick={() => setInviteDialogOpen(true)} className="gap-2">
              <UserPlus size={16} />
              Invite User
            </Button>
          </div>
        </div>
        
        {/* This is the main content area that needs conditional rendering */}
        {!messagingDialogOpen && (
          <div className="flex-1 p-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="members" className="flex items-center gap-2">
                  <UsersIcon size={16} />
                  Organization Members
                </TabsTrigger>
                <TabsTrigger value="organizations" className="flex items-center gap-2">
                  <Building2 size={16} />
                  My Organizations
                </TabsTrigger>
                <TabsTrigger value="knowledgebases" className="flex items-center gap-2">
                  <Share2 size={16} />
                  Shared Knowledge Bases
                </TabsTrigger>
                <TabsTrigger value="connections" className="flex items-center gap-2">
                  <UserCheck size={16} />
                  My Connections
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="members" className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xl font-semibold">Organization Members</h2>
                  <Button variant="outline" onClick={() => setManageMembersDialogOpen(true)} size="sm" className="gap-2">
                    <Shield size={16} />
                    Manage Permissions
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-48 w-full" />)
                  ) : users.length === 0 ? (
                     <Card className="col-span-full">
                      <CardContent className="flex flex-col items-center justify-center p-6 text-center">
                        <UsersIcon className="h-12 w-12 mb-2 text-muted-foreground/40" />
                        <p className="text-muted-foreground">No members in this organization yet.</p>
                        <p className="text-sm text-muted-foreground/70 mt-1">
                          Invite users to start collaborating.
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    users.map(userObj => {
                      console.log(`Checking user ${userObj.name} (ID: ${userObj.id}). Is online? ${onlineUsers.has(userObj.id)}`);
                      return (
                        <Card key={userObj.id} className="overflow-hidden">
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <Avatar className="h-12 w-12">
                                  {userObj.avatar ? (
                                    <AvatarImage src={userObj.avatar} alt={userObj.name} />
                                  ) : (
                                    <AvatarFallback>{userObj.name?.charAt(0) || 'U'}</AvatarFallback>
                                  )}
                                </Avatar>
                                <div>
                                  <CardTitle className="text-md">{userObj.name}</CardTitle>
                                  <CardDescription className="text-xs">{userObj.email}</CardDescription>
                                </div>
                              </div>
                              <Badge variant={getRoleBadgeVariant(userObj.role)}>
                                {userObj.role}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Status</span>
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${getStatusColor(userObj.status)}`} />
                                  <span>{userObj.status}</span>
                                </div>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Last active</span>
                                <span>{userObj.lastActive}</span>
                              </div>
                              <div className="flex gap-2 pt-2">
                                {userObj.id !== user?.id && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="flex-1 text-xs"
                                      onClick={() => handleConnect(userObj.id, userObj.name)}
                                    >
                                      <UserCheck size={14} className="mr-1" />
                                      Connect
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="flex-1 text-xs"
                                      onClick={() => handleStartMessaging(userObj.id, userObj.name)}
                                    >
                                      <MessageSquare size={14} className="mr-1" />
                                      Message
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="organizations" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {orgsLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <Card key={`skeleton-${i}`}>
                        <CardHeader>
                          <Skeleton className="h-6 w-3/4 mb-2" />
                          <Skeleton className="h-4 w-1/2" />
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Skeleton className="h-4 w-1/4" />
                              <Skeleton className="h-4 w-1/4" />
                            </div>
                            <div className="flex items-center justify-between">
                              <Skeleton className="h-4 w-1/3" />
                              <Skeleton className="h-4 w-1/4" />
                            </div>
                          </div>
                        </CardContent>
                        <CardFooter>
                          <Skeleton className="h-10 w-full" />
                        </CardFooter>
                      </Card>
                    ))
                  ) : organizations.length === 0 ? (
                    <Card className="col-span-full">
                      <CardContent className="flex flex-col items-center justify-center p-6 text-center">
                        <Building2 className="h-12 w-12 mb-2 text-muted-foreground/40" />
                        <p className="text-muted-foreground">No organizations yet</p>
                        <p className="text-sm text-muted-foreground/70 mt-1">
                          Create an organization to collaborate with others
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    organizations.map(org => (
                      <Card 
                        key={org.id} 
                        className={`overflow-hidden ${activeOrganization?.id === org.id ? 'border-primary' : ''}`}
                        onClick={() => setActiveOrganization(org)}
                      >
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle>{org.name}</CardTitle>
                              <CardDescription>{org.description || 'No description'}</CardDescription>
                            </div>
                            <Badge variant={getRoleBadgeVariant(getUserRoleInOrg(org))}>
                              {getUserRoleInOrg(org)}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Members</span>
                              <span className="font-medium">{org.members_count || 0}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Created</span>
                              <span className="font-medium">{format(new Date(org.created_at), 'PP')}</span>
                            </div>
                          </div>
                        </CardContent>
                        <CardFooter className="border-t pt-4 flex justify-between">
                          <Button variant="outline" size="sm" onClick={(e) => {
                            e.stopPropagation();
                            setInviteDialogOpen(true);
                          }}>
                            <UserPlus size={16} className="mr-2" />
                            Invite Users
                          </Button>
                          <Button variant="outline" size="sm" onClick={(e) => {
                            e.stopPropagation();
                            setShareKbDialogOpen(true);
                          }}>
                            <Share2 size={16} className="mr-2" />
                            Share KB
                          </Button>
                        </CardFooter>
                      </Card>
                    ))
                  )}
                  
                  <Card className="flex flex-col items-center justify-center h-full border-dashed cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setOrganizationDialogOpen(true)}>
                    <CardContent className="flex flex-col items-center justify-center py-8">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                        <Building2 className="h-6 w-6 text-primary" />
                      </div>
                      <p className="font-medium">Create New Organization</p>
                      <p className="text-sm text-muted-foreground text-center mt-1">
                        Set up a new workspace for your team
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="knowledgebases" className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xl font-semibold">Knowledge Bases in {activeOrganization?.name || 'Your Organization'}</h2>
                  <Button variant="outline" onClick={() => setShareKbDialogOpen(true)} size="sm" className="gap-2">
                    <Share2 size={16} />
                    Share Knowledge Base
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {kbLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <Card key={`skeleton-${i}`}>
                        <CardHeader>
                          <Skeleton className="h-6 w-3/4 mb-2" />
                          <Skeleton className="h-4 w-1/2" />
                        </CardHeader>
                        <CardContent>
                          <Skeleton className="h-4 w-full" />
                        </CardContent>
                        <CardFooter>
                          <Skeleton className="h-10 w-full" />
                        </CardFooter>
                      </Card>
                    ))
                  ) : knowledgeBases.length === 0 ? (
                    <Card className="col-span-full">
                      <CardContent className="flex flex-col items-center justify-center p-6 text-center">
                        <UserX className="h-12 w-12 mb-2 text-muted-foreground/40" />
                        <p className="text-muted-foreground">No knowledge bases yet</p>
                        <p className="text-sm text-muted-foreground/70 mt-1">
                          Create or share knowledge bases to collaborate with others
                        </p>
                      </CardContent>
                    </Card>
                  ) : renderKnowledgeBases()}
                </div>
              </TabsContent>
              
              <TabsContent value="connections" className="space-y-4">
                <UserConnections onStartMessaging={handleStartMessaging} />
              </TabsContent>
            </Tabs>
          </div>
        )}
        
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Invite User</DialogTitle>
              <DialogDescription>
                Send an invitation to join {activeOrganization?.name || "your organization"}.
              </DialogDescription>
            </DialogHeader>
            <Form {...inviteForm}>
              <form onSubmit={inviteForm.handleSubmit(handleInviteUser)} className="space-y-4">
                <FormField
                  control={inviteForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="user@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={inviteForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={inviteForm.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>Role</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex flex-col space-y-1"
                        >
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="Member" />
                            </FormControl>
                            <FormLabel className="font-normal">
                              Member (can use shared resources)
                            </FormLabel>
                          </FormItem>
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="Admin" />
                            </FormControl>
                            <FormLabel className="font-normal">
                              Admin (can invite users and manage resources)
                            </FormLabel>
                          </FormItem>
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="Owner" />
                            </FormControl>
                            <FormLabel className="font-normal">
                              Owner (full control of organization)
                            </FormLabel>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={inviteForm.control}
                  name="sendEmail"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          Send email invitation
                        </FormLabel>
                        <FormDescription>
                          An email will be sent with instructions to join.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
                <FormField
                  control={inviteForm.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Personal Message (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Add a personal note to the invitation" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button type="submit">Send Invitation</Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
        
        <Dialog open={organizationDialogOpen} onOpenChange={setOrganizationDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Create Organization</DialogTitle>
              <DialogDescription>
                Create a new organization for your team.
              </DialogDescription>
            </DialogHeader>
            <Form {...organizationForm}>
              <form onSubmit={organizationForm.handleSubmit(handleCreateOrganization)} className="space-y-4">
                <FormField
                  control={organizationForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organization Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Acme Corp" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={organizationForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Brief description of your organization" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button type="submit">Create Organization</Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
        
        <Dialog open={shareKbDialogOpen} onOpenChange={setShareKbDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Share Knowledge Base</DialogTitle>
              <DialogDescription>
                Share a knowledge base with {activeOrganization?.name || "your organization"}.
              </DialogDescription>
            </DialogHeader>
            <Form {...shareKbForm}>
              <form onSubmit={shareKbForm.handleSubmit(handleShareKnowledgeBase)} className="space-y-4">
                <FormField
                  control={shareKbForm.control}
                  name="knowledgeBaseId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Knowledge Base</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a knowledge base" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {knowledgeBases.map(kb => (
                            <SelectItem key={kb.id} value={kb.id}>{kb.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={shareKbForm.control}
                  name="accessLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Access Level</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select access level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="View">View (can only read)</SelectItem>
                          <SelectItem value="Edit">Edit (can modify content)</SelectItem>
                          <SelectItem value="Manage">Manage (full control)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button type="submit">Share Knowledge Base</Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
        
        <Dialog open={manageMembersDialogOpen} onOpenChange={setManageMembersDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Manage Member Permissions</DialogTitle>
              <DialogDescription>
                Configure access and roles for members in {activeOrganization?.name || "your organization"}.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[400px] overflow-y-auto space-y-4">
              {users.map(userObj => {
                const orgRole = userObj.role || 'Member';
                return (
                  <div key={userObj.id} className="flex items-center justify-between border-b pb-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        {userObj.avatar ? (
                          <AvatarImage src={userObj.avatar} alt={userObj.name} />
                        ) : (
                          <AvatarFallback>{userObj.name?.charAt(0) || 'U'}</AvatarFallback>
                        )}
                      </Avatar>
                      <div>
                        <p className="font-medium">{userObj.name}</p>
                        <p className="text-sm text-muted-foreground">{userObj.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Select defaultValue={orgRole}>
                        <SelectTrigger className="w-[110px]">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Member">Member</SelectItem>
                          <SelectItem value="Admin">Admin</SelectItem>
                          <SelectItem value="Owner">Owner</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-2">
                        <Switch id={`active-${userObj.id}`} defaultChecked={userObj.status === 'Active'} />
                        <label htmlFor={`active-${userObj.id}`} className="text-sm">
                          Active
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button onClick={() => setManageMembersDialogOpen(false)}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        <Dialog open={messagingDialogOpen} onOpenChange={setMessagingDialogOpen}>
          <DialogContent className="sm:max-w-[500px] h-[600px] flex flex-col p-0">
            <DialogHeader className="py-3 px-6 border-b flex-shrink-0">
              <DialogTitle>Message {selectedUser?.name}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden">
              {selectedUser && (
                <UserMessaging 
                  userId={selectedUser.id} 
                  userName={selectedUser.name} 
                  onClose={() => setMessagingDialogOpen(false)}
                  hideInternalHeader={true}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default Users;